import PropTypes from 'prop-types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ReactKeycloakProvider } from '@react-keycloak/web';

import AuthenticationLoading, { AUTHENTICATION_TIMEOUT_MS } from 'utils/AuthenticationLoading';
import ErrorPage from 'utils/ErrorPage';
import { createKeycloakClient } from './keycloak';

const AuthenticationProvider = ({ children }) => {
    const [attempt, setAttempt] = useState(0);
    const [initializationFailed, setInitializationFailed] = useState(false);
    const [authClient, setAuthClient] = useState(createKeycloakClient);
    const activeAttemptRef = useRef(0);
    const initializationCompleteRef = useRef(false);
    const initializationTimeoutRef = useRef(null);
    const mountedRef = useRef(true);

    const clearInitializationTimeout = useCallback(() => {
        if (!initializationTimeoutRef.current) return;
        window.clearTimeout(initializationTimeoutRef.current);
        initializationTimeoutRef.current = null;
    }, []);

    useEffect(() => {
        const timeoutAttempt = attempt;
        initializationTimeoutRef.current = window.setTimeout(() => {
            if (!mountedRef.current || initializationCompleteRef.current || timeoutAttempt !== activeAttemptRef.current) return;

            console.warn('[TMS authentication] Initialization timed out.');
            setInitializationFailed(true);
        }, AUTHENTICATION_TIMEOUT_MS);

        if (initializationCompleteRef.current) clearInitializationTimeout();

        return clearInitializationTimeout;
    }, [attempt, clearInitializationTimeout]);

    useEffect(() => {
        mountedRef.current = true;

        return () => {
            mountedRef.current = false;
            clearInitializationTimeout();
        };
    }, [clearInitializationTimeout]);

    const handleAuthEvent = useCallback(
        (eventType, error) => {
            if (attempt !== activeAttemptRef.current) return;

            if (eventType === 'onReady' || eventType === 'onAuthSuccess') {
                initializationCompleteRef.current = true;
                clearInitializationTimeout();
                return;
            }

            if (eventType !== 'onInitError' && eventType !== 'onAuthError') return;

            clearInitializationTimeout();
            console.error(`[TMS authentication] ${eventType}.`, {
                name: error?.name || 'AuthenticationError'
            });
            setInitializationFailed(true);
        },
        [attempt, clearInitializationTimeout]
    );

    const handleRetry = () => {
        const nextAttempt = activeAttemptRef.current + 1;

        clearInitializationTimeout();
        activeAttemptRef.current = nextAttempt;
        initializationCompleteRef.current = false;
        setInitializationFailed(false);
        setAuthClient(createKeycloakClient());
        setAttempt(nextAttempt);
    };

    if (initializationFailed) {
        return (
            <ErrorPage
                status="authentication"
                title="We couldn’t start secure sign-in"
                description="The authentication service did not respond as expected. Try again, or reload the application to start over."
                onRetry={handleRetry}
                onSecondaryAction={() => window.location.reload()}
                secondaryActionLabel="Reload application"
            />
        );
    }

    return (
        <ReactKeycloakProvider key={attempt} authClient={authClient} LoadingComponent={<AuthenticationLoading />} onEvent={handleAuthEvent}>
            {children}
        </ReactKeycloakProvider>
    );
};

AuthenticationProvider.propTypes = {
    children: PropTypes.node.isRequired
};

export default AuthenticationProvider;
